import React from 'react';
import { LayoutPage, LayoutContent, Errors, FormButtons, Form } from 'components';
import { Container, Message, List, Checkbox, Text, Input, Link } from 'basis';
import { submitHandler, getDataLayerElements, isNZ } from 'utils';
import { useStoreValue, APOLLO_NETWORK_ERROR, APOLLO_UNKNOWN_ERROR } from 'store';
import { useEventTracking } from 'react-event-tracker';
import { useSteps, useErrorTrackingAndUpdateStore, useFormInitialValues, useDeliverApplication } from 'hooks';
import { numbersOnlyPattern, OverviewSources } from '_config/_constants';
import { minCreditLimit, creditLimitIncrement, maxCreditLimit } from '_config/nz/constants';
import { validation } from '_config/_validation/validation';
import { formatNumber } from 'utils/formatNumber';
import { useHistory } from 'react-router-dom';

const FORM_ID = 'creditLimitAcceptance';

const validator = validation(
  {
    minValue: minCreditLimit,
    maxValue: maxCreditLimit,
    minLength: 3,
    maxLength: 6,
    incrementValue: creditLimitIncrement,
    validPattern: numbersOnlyPattern,
  },
  {
    required: 'Enter your preferred credit limit first.',
    minValue: `Please enter a credit limit amount of \${{minValue}} or more in \${{incrementValue}} increments.`,
    maxValue: `The maximum amount you can enter is $${maxCreditLimit}.`,
    minLength: 'Must have at least {{minLength}} digits.',
    maxLength: 'Must have at most {{maxLength}} digits.',
    incrementValue: `Please enter an amount in \${{incrementValue}} increments.`,
    invalid: 'Please enter a whole dollar amount, without cents.',
  },
)[0];

export const CreditLimitAcceptance = () => {
  const [storeState, updateStore] = useStoreValue();
  const { trackEvent } = useEventTracking();
  const history = useHistory();

  const { handleErrorCallback } = useErrorTrackingAndUpdateStore('Next');
  const { isLastDraftStep, toNext, nextStep } = useSteps();
  const initialValues = useFormInitialValues(FORM_ID);

  const onChangeCreditLimit = formData => {
    formData.setValues(values => ({
      ...values,
      accepted: false,
    }));
  };

  const { deliver, loading: loadingDeliverApplication } = useDeliverApplication({
    storeState,
    onSuccess: () => {
      trackEvent({
        event: {
          category: 'application',
          action: 'application-submitted',
          location: 'credit-limit-details',
          label: 'Next',
        },
        ...getDataLayerElements({
          ...storeState,
          applicationSubmitted: true,
        }),
      });
      updateStore({
        activeStep: nextStep,
        applicationSubmitted: true,
      });
      history.push(nextStep);
    },
    onError: handleErrorCallback,
  });

  const onSubmit = ({ values, errors }) => {
    function submit() {
      const newAcquisition = {
        ...storeState.acquisition,
        creditLimitAcceptance: {
          creditLimit: parseInt(values.creditLimit, 10),
          accepted: values.accepted,
        },
      };
      const applicationSubmitted = isLastDraftStep || isNZ();

      updateStore({
        applicationSubmitted,
        acquisition: newAcquisition,
      });

      const dataLayer = getDataLayerElements({
        ...storeState,
        applicationSubmitted,
        acquisition: newAcquisition,
      });

      trackEvent({
        event: {
          category: 'application',
          action: applicationSubmitted ? 'application-submitted' : 'application-navigation',
          location: 'credit-limit-details',
          label: 'Next',
          value: values.creditLimit,
        },
        ...dataLayer,
      });

      if (isLastDraftStep) {
        deliver(newAcquisition);
      } else {
        toNext();
      }
    }

    submitHandler({ submit, errors });
  };

  const retrySubmit = () => {
    trackEvent({
      event: {
        category: 'application',
        action: isLastDraftStep ? 'application-submitted' : 'application-navigation',
        location: 'credit-limit-details',
        label: 'Retry',
      },
    });

    deliver(storeState.acquisition);
  };

  if (storeState.applicationErrors) {
    const retry = [APOLLO_NETWORK_ERROR, APOLLO_UNKNOWN_ERROR].includes(storeState.applicationErrors.type)
      ? retrySubmit
      : null;
    return (
      <LayoutPage>
        <Errors applicationRef={storeState.applicationRef} retry={retry} retrying={loadingDeliverApplication} />
      </LayoutPage>
    );
  }

  return (
    <LayoutPage>
      <Container margin="0 0 13 0" hasBreakpointWidth>
        <Form
          id={FORM_ID}
          initialValues={initialValues}
          onSubmit={onSubmit}
          submitButtonLabel={isNZ() ? 'Accept credit card limit and submit' : 'Next'}
          saveDraftOnSubmit={!isNZ()}
          saveDraftOnSessionTimeout
          hideFormButtons
        >
          {formData => {
            return (
              <>
                <LayoutContent rowsGap="0" noSessionTimeout>
                  <Container margin="6 0 2 0">
                    <Input
                      label="To meet my needs and objectives I would like a credit limit of"
                      variant="numeric"
                      name="creditLimit"
                      key="creditLimit"
                      testId="creditLimit"
                      prefix="$"
                      maxLength={6}
                      validate={validator}
                      validateData={formData.state}
                      onChange={() => onChangeCreditLimit(formData)}
                    />
                  </Container>
                  <Text>
                    This credit limit amount is subject to finalisation of your application and responsible lending
                    requirements.
                  </Text>
                  <Container margin="6 0">
                    <Message margin="8 0 0 0" severity="info-or-minor" bg="secondary.lightBlue.t25">
                      You can always contact us in the future to request a decrease or increase to your credit limit if
                      your circumstances change.
                    </Message>
                  </Container>
                  {!isNZ() && (
                    <>
                      <Container margin="0 0 4 0">
                        <Checkbox
                          name="accepted"
                          testId="accepted"
                          validate={value => (value === false ? 'You must agree before you continue' : null)}
                        >
                          <>
                            By clicking here I confirm that:
                            <Container padding="3 0">
                              <List>
                                <List.Item>
                                  I've read and agree to the{' '}
                                  <Link newTab href={OverviewSources.NZ.gem_visa_conditions_of_use.url}>
                                    {OverviewSources.NZ.gem_visa_conditions_of_use?.label}
                                  </Link>{' '}
                                  and{' '}
                                  <Link newTab href={OverviewSources.NZ.initial_disclosure_statement.url}>
                                    {OverviewSources.NZ.initial_disclosure_statement.label}
                                  </Link>{' '}
                                  (read in conjunction with the credit limit noted below).
                                </List.Item>
                                <List.Item>
                                  $
                                  {formData.state.values.creditLimit === ''
                                    ? 0
                                    : formatNumber(formData.state.values.creditLimit)}{' '}
                                  is my preferred credit limit and this meets my needs and objectives.
                                </List.Item>
                                <List.Item>
                                  I understand that I may receive a lower credit limit on the completion of the
                                  assessment of my application and that my final credit limit will be confirmed to me by
                                  email.
                                </List.Item>
                                <List.Item>
                                  I understand that I have the right to cancel my credit contract within 10 working
                                  days.
                                </List.Item>
                              </List>
                            </Container>
                          </>
                        </Checkbox>
                      </Container>
                    </>
                  )}
                  {isNZ() && (
                    <>
                      <Text textStyle="heading5">Before you continue, please confirm:</Text>
                      <Container margin="0 0 0 2">
                        <Container padding="3 0">
                          <List>
                            <List.Item>
                              I've read and agree to the{' '}
                              <Link newTab href={OverviewSources.NZ.gem_visa_conditions_of_use.url}>
                                {OverviewSources.NZ.gem_visa_conditions_of_use?.label}
                              </Link>{' '}
                              and{' '}
                              <Link newTab href={OverviewSources.NZ.initial_disclosure_statement.url}>
                                {OverviewSources.NZ.initial_disclosure_statement.label}
                              </Link>{' '}
                              (read in conjunction with the credit limit noted above).
                            </List.Item>
                            <List.Item>
                              $
                              {formData.state.values.creditLimit === ''
                                ? 0
                                : formatNumber(formData.state.values.creditLimit)}{' '}
                              is my preferred credit limit and this meets my needs and objectives.
                            </List.Item>
                            <List.Item>
                              I understand that I may receive a lower credit limit on the completion of the assessment
                              of my application and that my final credit limit will be confirmed to me by email.
                            </List.Item>
                            <List.Item>
                              I understand that I have the right to cancel my credit contract within 10 working days.
                            </List.Item>
                          </List>
                        </Container>
                      </Container>
                      <Container margin="0 0 0 2">
                        <Checkbox
                          name="accepted"
                          testId="accepted"
                          validate={value => (value === false ? 'You must agree before you continue' : null)}
                        >
                          I confirm that I have read and agree to the above terms, conditions and disclosures.
                        </Checkbox>
                      </Container>
                      <Container margin="0 0 0 2">
                        <Text textStyle="legal" margin="6 0">
                          For your future reference, once your application has been finalised we'll email you a copy of
                          the Conditions of Use and your Initial Disclosure Statement which will detail your approved
                          credit limit.
                        </Text>
                      </Container>
                    </>
                  )}
                </LayoutContent>
                <FormButtons
                  formId={FORM_ID}
                  formData={formData}
                  loading={loadingDeliverApplication}
                  submitButtonLabel={isNZ() ? 'Accept credit card limit and submit' : 'Next'}
                  showBackButton
                />
              </>
            );
          }}
        </Form>
      </Container>
    </LayoutPage>
  );
};
